/**
 * One room band — a port of the generator's `roomHtml` and `roomMetaHtml`.
 *
 * The percentage on this line is `room.pct` straight from the shared
 * `buildModel`. It is not recalculated here, and it must not be: the API
 * computes the same figure with the same function, and a second implementation
 * in the browser is exactly how the two would drift apart.
 *
 * Note the pillar count comes from `terms.plural('pillar')` rather than the
 * generator's `pillar${n===1?'':'s'}`. A client who renames Pillar to "Phase of
 * Analysis" has no suffixable plural, so the "s" is never appended.
 */
import type { RoomModel } from '@blueprint/shared';
import { useTerms } from '../../lib/terms';
import { Pillar } from './Pillar';

/** The generator's `fmtWeight`: whole numbers stay whole, otherwise one decimal. */
export function fmtWeight(w: number): string {
  const pct = w * 100;
  return Math.abs(pct - Math.round(pct)) < 0.05 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}

export function RoomMeta({ room }: { room: RoomModel }) {
  const terms = useTerms();
  const count = room.pillars.length;
  const word = count === 1 ? terms.noun('pillar') : terms.plural('pillar');

  return (
    <div className="room-meta">
      <b>{room.pct}%</b> complete
      {room.weight ? ` · weight ${fmtWeight(room.weight)}` : ''} · {count} {word.toLowerCase()}
    </div>
  );
}

export function RoomBand({
  room,
  highlighted,
  onCycle,
}: {
  room: RoomModel;
  highlighted: ReadonlySet<number>;
  onCycle?: (index: number) => void;
}) {
  return (
    <div className="room">
      <h3>{room.RoomName}</h3>
      <RoomMeta room={room} />
      <div className="pillars">
        {room.pillars.map((pillar, index) => (
          <Pillar
            pillar={pillar}
            highlighted={highlighted}
            {...(onCycle ? { onCycle } : {})}
            key={`${pillar.name}-${index}`}
          />
        ))}
      </div>
    </div>
  );
}
